import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface TranslationContextType {
    language: string;
    setLanguage: (lang: string) => void;
    isRTL: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
    children: ReactNode;
}

// Storage key for persisting language preference
const LANGUAGE_STORAGE_KEY = 'preferred_language';

// RTL (Right-to-Left) languages
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur', 'yi', 'ps', 'sd', 'ug', 'dv'];

// Staff/internal paths — language changes must not affect layout here
const STAFF_PATHS = ['/login', '/staff', '/admin', '/research'];
function isStaffPath() {
    return STAFF_PATHS.some(
        p => window.location.pathname === p || window.location.pathname.startsWith(p + '/')
    );
}

export function TranslationProvider({ children }: TranslationProviderProps) {
    // Initialize from localStorage or default to 'en'
    const [language, setLanguageState] = useState<string>(() => {
        try {
            return localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en';
        } catch {
            return 'en';
        }
    });

    // Check if current language is RTL
    const isRTL = RTL_LANGUAGES.includes(language);

    // Update document direction and lang attribute when language changes
    useEffect(() => {
        // Never apply RTL/lang overrides on staff/admin/login pages
        if (isStaffPath()) {
            document.documentElement.lang = 'en';
            document.documentElement.dir = 'ltr';
            document.documentElement.classList.remove('rtl');
            document.documentElement.classList.forEach(cls => {
                if (cls.startsWith('lang-')) document.documentElement.classList.remove(cls);
            });
            document.documentElement.classList.add('lang-en');
            return;
        }

        document.documentElement.lang = language;
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';

        // Add RTL class for custom styling if needed
        if (isRTL) {
            document.documentElement.classList.add('rtl');
        } else {
            document.documentElement.classList.remove('rtl');
        }

        // Add lang-specific class for font overrides (e.g. lang-dv for MVWaheed)
        document.documentElement.classList.forEach(cls => {
            if (cls.startsWith('lang-')) document.documentElement.classList.remove(cls);
        });
        document.documentElement.classList.add(`lang-${language}`);
    }, [language, isRTL]);

    // Wrapper to persist language to localStorage
    const setLanguage = (lang: string) => {
        // Clear translation cache when changing languages
        try {
            localStorage.removeItem('auto_translate_cache');
        } catch (err) {
            console.error('Failed to clear translation cache:', err);
        }
        
        // If switching to English, clear cache and reload to get fresh content
        // This ensures we don't show cached translated text as "originals"
        if (lang === 'en' && language !== 'en') {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, 'en');
            window.location.reload();
            return;
        }
        
        setLanguageState(lang);
        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        } catch (err) {
            console.error('Failed to save language preference:', err);
        }
    };

    // Also sync from localStorage on mount (in case of multiple tabs)
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === LANGUAGE_STORAGE_KEY && e.newValue) {
                setLanguageState(e.newValue);
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Reset DOM direction/lang whenever the user navigates to a staff page
    useEffect(() => {
        const resetForStaff = () => {
            if (isStaffPath()) {
                document.documentElement.lang = 'en';
                document.documentElement.dir = 'ltr';
                document.documentElement.classList.remove('rtl');
                document.documentElement.classList.forEach(cls => {
                    if (cls.startsWith('lang-')) document.documentElement.classList.remove(cls);
                });
                document.documentElement.classList.add('lang-en');
            }
        };
        window.addEventListener('popstate', resetForStaff);
        // React Router uses pushState — patch it once
        const origPush = history.pushState.bind(history);
        history.pushState = (...args) => { origPush(...args); resetForStaff(); };
        resetForStaff(); // run on mount too
        return () => {
            window.removeEventListener('popstate', resetForStaff);
            history.pushState = origPush;
        };
    }, []);

    return (
        <TranslationContext.Provider value={{ language, setLanguage, isRTL }}>
            {children}
        </TranslationContext.Provider>
    );
}

export function useTranslation() {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within TranslationProvider');
    }
    return context;
}
